/**
 * Where a voice-note transcript lands in the caption.
 *
 * Same rule as the app (`src/utils/transcriptPlacement.ts`), keep them in
 * parity:
 *
 * 1. The editor chose a spot: a caret or selection in the caption textarea
 *    that still fits the current text. The transcript replaces the
 *    selection (or is inserted at the caret) with the spacing fixed up.
 * 2. Otherwise the persons slot: the caption still carries the persons
 *    phrase the template rendered ("A of X, B of Y"), so the transcript
 *    takes its place, exactly where the Caption Builder writes its phrase.
 * 3. Otherwise appended with one space.
 *
 * Pure string work: the caller passes the persons phrase it already knows.
 * The caption is never rendered from the template here.
 */

function validSelection(sel, length) {
  if (!sel) return false;
  const { start, end } = sel;
  if (!Number.isInteger(start) || !Number.isInteger(end)) return false;
  return start >= 0 && end >= start && end <= length;
}

function insertAt(caption, start, end, text) {
  const before = caption.slice(0, start);
  const after = caption.slice(end);
  const needsSpaceBefore = before.length > 0 && !/\s$/.test(before);
  const needsSpaceAfter = after.length > 0 && !/^[\s.,;:!?)]/.test(after);
  return `${before}${needsSpaceBefore ? ' ' : ''}${text}${needsSpaceAfter ? ' ' : ''}${after}`;
}

/**
 * @param {string} caption
 * @param {string} transcript
 * @param {{ selection?: {start:number,end:number}|null, personsPhrase?: string|null }} [options]
 * @returns {{ caption: string, mode: 'caret'|'slot'|'append'|'none' }}
 */
export function placeTranscript(caption, transcript, options = {}) {
  const text = (transcript ?? '').trim();
  const base = caption ?? '';
  if (!text) return { caption: base, mode: 'none' };

  const sel = options.selection ?? null;
  if (validSelection(sel, base.length)) {
    return { caption: insertAt(base, sel.start, sel.end, text), mode: 'caret' };
  }

  const phrase = (options.personsPhrase ?? '').trim();
  if (phrase) {
    const at = base.indexOf(phrase);
    if (at >= 0) {
      return { caption: insertAt(base, at, at + phrase.length, text), mode: 'slot' };
    }
  }

  if (!base.trim()) return { caption: text, mode: 'append' };
  return { caption: `${base.replace(/\s+$/, '')} ${text}`, mode: 'append' };
}
